import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Title, Subtitle, BodyText } from '../../components/ui';
import Layout from '../../components/Layout';
import ListView from '../../components/ui/ListView';
import { getECatalogList } from '../../api/services/systemService';
import { formatTitle } from '../../utils';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 90vh;
  padding: 20px;
  position: relative;
`;

const BannerTitle = styled(Title)`
  color: black;
  margin-bottom: 30px;
`;

const CatalogCard = styled.div`
  width: 180px;
  height: 260px;
  background: #f8f9fa;
  border-radius: 15px;
  padding: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition:
    transform 0.3s ease,
    box-shadow 0.3s ease,
    background-color 0.3s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  }

  @media (max-width: 1024px) {
    width: 200px;
    height: 240px;
  }

  @media (max-width: 768px) {
    width: 160px;
    height: 200px;
  }

  @media (max-width: 480px) {
    width: 140px;
    height: 180px;
  }
`;

const CardImage = styled.img`
  width: 140px;
  height: 220px;
  object-fit: contain;

  @media (max-width: 1024px) {
    width: 180px;
    height: 180px;
  }

  @media (max-width: 768px) {
    width: 140px;
    height: 140px;
  }

  @media (max-width: 480px) {
    width: 120px;
    height: 120px;
  }
`;

const CardTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #333;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: center;
  padding: 0 4px;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 1024px) {
    max-width: 180px;
    font-size: 13px;
  }

  @media (max-width: 768px) {
    max-width: 140px;
    font-size: 12px;
  }

  @media (max-width: 480px) {
    max-width: 120px;
    font-size: 11px;
  }
`;

const ECatalogPage = () => {
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    const fetchECatalogData = async () => {
      try {
        const response = await getECatalogList();

        // Extract the eCatalogList array from the response
        const data = response?.eCatalogList || response || [];

        setCategories(data);
      } catch (err) {
        // On error, just set empty array to show blank grid
        setCategories([]);
      }
    };

    fetchECatalogData();
  }, []);

  const handleCategoryClick = (item) => {
    if (item.filepath2) {
      // Construct full PDF URL
      const pdfUrl = item.filepath2.startsWith('http')
        ? item.filepath2
        : `${process.env.REACT_APP_FILE_UPLOAD_URL || ''}${item.filepath2}`;

      window.open(pdfUrl, '_blank');
    }
  };

  const renderCategoryItem = ({ item }) => {
    // Construct full image URL
    const getImageUrl = (filepath) => {
      if (!filepath) return '';
      if (filepath.startsWith('http')) return filepath;
      return `${process.env.REACT_APP_FILE_UPLOAD_URL || ''}${filepath}`;
    };

    return (
      <div style={{ textAlign: 'center' }}>
        <CatalogCard onClick={() => handleCategoryClick(item)}>
          <CardImage
            src={getImageUrl(item.Tfilepath1)}
            alt='E-Catalog Item'
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
          {item.title && <CardTitle>{formatTitle(item.title)}</CardTitle>}
        </CatalogCard>
      </div>
    );
  };

  return (
    <Layout>
      <Container>
        <BannerTitle>E-Catalogs</BannerTitle>

        <ListView
          data={categories}
          renderItem={renderCategoryItem}
          numColumns={(width) =>
            width <= 480 ? 2 : width <= 768 ? 3 : width <= 1024 ? 4 : 5
          }
          rowGap={'20px'}
          columnGap={'20px'}
        />
      </Container>
    </Layout>
  );
};

export default ECatalogPage;
